import { SelectedFile } from 'apis/attached_files'
import {
  AttachedFile,
  AttachedFiles,
  SamplePostAttachedFiles
} from 'apis/response_types'
import React from 'react'
import styled from 'styled-components'
import Cancel from '@material-ui/icons/Cancel'
import IconUploadFile from '../../../images/icon_uploadfile.svg'
import IconDownloadFile from '../../../images/icon_downloadfile.svg'

const getFileExtension = (filename: string) => {
  return filename.split('.').pop()
}

export const VideoFilePreview = React.memo(
  (props: { file: File; cancelFunction: () => void }) => {
    const { file, cancelFunction } = props

    return (
      <SelectedUploadedFile>
        <StyledCancel onClick={() => cancelFunction()} />
        <SelectedUploadedFileDetail>
          <img src={IconUploadFile} alt="IconUploadFile" />
          <div>
            <SelectedUploadedFileName>{file.name}</SelectedUploadedFileName>
            <p>{getFileExtension(file.name)}</p>
          </div>
        </SelectedUploadedFileDetail>
      </SelectedUploadedFile>
    )
  }
)

export const SelectFilesPreview = React.memo(
  (props: {
    files: Array<SelectedFile>
    uploadedFiles?: Array<AttachedFile>
    cancelFunction: (filename: string) => void
    deleteFileFunction?: (id: number) => void
  }) => {
    const { files, uploadedFiles, cancelFunction, deleteFileFunction } = props

    return (
      <>
        {files.map((file: SelectedFile, index: number) => {
          return (
            <React.Fragment key={file.name + index}>
              <SelectedUploadedFile>
                <StyledCancel onClick={() => cancelFunction(file.name)} />
                <SelectedUploadedFileDetail>
                  <img src={IconUploadFile} alt="IconUploadFile" />
                  <div>
                    <SelectedUploadedFileName>
                      {file.name}
                    </SelectedUploadedFileName>
                    <p>{getFileExtension(file.selectedFile.name)}</p>
                  </div>
                </SelectedUploadedFileDetail>
              </SelectedUploadedFile>
            </React.Fragment>
          )
        })}
        {uploadedFiles &&
          uploadedFiles.map((file) => {
            return (
              <React.Fragment key={file.id}>
                <SelectedUploadedFile>
                  <StyledCancel
                    onClick={() =>
                      deleteFileFunction && deleteFileFunction(file.id)
                    }
                  />
                  <SelectedUploadedFileDetail>
                    <img src={IconUploadFile} alt="IconUploadFile" />
                    <div>
                      <SelectedUploadedFileName>
                        {file.filename}
                      </SelectedUploadedFileName>
                      <p>{getFileExtension(file.filename)}</p>
                    </div>
                  </SelectedUploadedFileDetail>
                </SelectedUploadedFile>
              </React.Fragment>
            )
          })}
      </>
    )
  }
)

export const FilesPreview = React.memo(
  (props: { attachedFiles: AttachedFiles }) => {
    const attachedFiles = props.attachedFiles

    const FilePreview = (file: AttachedFile) => {
      return (
        <React.Fragment key={file.id}>
          <DownloadFileList>
            <DownloadFileListItem>
              <DownloadFileDetail>
                <DownloadFileName href={file.url}>
                  {file.filename}
                </DownloadFileName>
                <p>{getFileExtension(file.filename)}</p>
              </DownloadFileDetail>
              <a href={file.url} rel="nofollow">
                <img src={IconDownloadFile} alt="" />
              </a>
            </DownloadFileListItem>
          </DownloadFileList>
        </React.Fragment>
      )
    }

    return (
      <>
        {attachedFiles.audios.map((file: AttachedFile) => FilePreview(file))}
        {attachedFiles.documents.map((file: AttachedFile) => FilePreview(file))}
        {attachedFiles.files.map((file: AttachedFile) => FilePreview(file))}
      </>
    )
  }
)

export const SamplePostFilesPreview = React.memo(
  (props: { attachedFiles: SamplePostAttachedFiles; canDownload: boolean }) => {
    const { attachedFiles, canDownload } = props

    const FilePreview = (file: AttachedFile) => {
      return (
        <React.Fragment key={file.id}>
          <DownloadFileList>
            <DownloadFileListItem>
              <DownloadFileDetail>
                <DownloadFileName href={file.url}>
                  {file.filename}
                </DownloadFileName>
                <p>{getFileExtension(file.filename)}</p>
              </DownloadFileDetail>
              {canDownload && (
                <a href={file.url} rel="nofollow">
                  <img src={IconDownloadFile} alt="" />
                </a>
              )}
            </DownloadFileListItem>
          </DownloadFileList>
        </React.Fragment>
      )
    }

    return (
      <>
        {attachedFiles.audios.map((file: AttachedFile) => FilePreview(file))}
        {attachedFiles.documents.map((file: AttachedFile) => FilePreview(file))}
        {attachedFiles.files.map((file: AttachedFile) => FilePreview(file))}
      </>
    )
  }
)

const SelectedUploadedFile = styled.div`
  position: relative;
  border: 1px solid #c4c4c4;
  padding: 8px;
  border-radius: 10px;
  width: 176px;
  flex-shrink: 0;
  @media screen and (max-width: 640px) {
    width: 166px;
    padding: 8px 24px 8px 8px;
  }
  .MuiSvgIcon-root {
    @media screen and (max-width: 640px) {
      width: 30px !important;
      height: 30px;
    }
  }
`
const SelectedUploadedFileDetail = styled.div`
  display: flex;
  gap: 8px;
  p {
    font-size: 12px !important;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;
  }
  img {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    border-radius: 6px;
    @media screen and (max-width: 640px) {
      width: 58px;
      height: 58px;
    }
  }
`
const SelectedUploadedFileName = styled.p`
  font-size: 12px;
  font-weight: bold;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
`
const StyledCancel = styled(Cancel)`
  cursor: pointer;
  border: 0;
  background: transparent;
  position: absolute;
  right: -6px;
  top: -6px;
  padding: 0;
  background-color: #ffffff;
  border-radius: 20px;
`

const DownloadFileList = styled.ul`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 16px;
`
const DownloadFileListItem = styled.li`
  width: 548px;
  border: 1px solid #c4c4c4;
  padding: 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  @media screen and (max-width: 640px) {
    width: 100%;
  }
  img {
    vertical-align: bottom;
  }
`
const DownloadFileDetail = styled.div`
  display: flex;
  flex-direction: column;
  a,
  p {
    color: #333 !important;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;
  }
  a {
    font-size: 14px !important;
    font-weight: bold;
  }
  p {
    font-size: 10px !important;
  }
`
const DownloadFileName = styled.a`
  font-size: 12px;
  font-weight: bold;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
`
